import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './sectionDialog.module.scss'
import { CREATE_SECTION, UPDATE_SECTION } from '@/modules/API/daycare/sectionHandler'
import { useMutation } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import UserInput from '@/components/_custom/UserInput/userInput'
import { SectionGroup } from '@/types/Section'
import { Daycare, DaycareType } from '@/types/Daycare'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import StaffRateDialog from '../../../../staff/modals/staffRate/Form/staffRateForm'
import { SET_SECTION_GROUP_TO_SECTION } from '@/modules/API/staff/staffRate'
import { SelectOption, SelectOptions } from '@/types/Select'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useDialog } from '@/hooks/useDialog/useDialog'

interface sectionDialogDialogProps {
    open: boolean
    handleClose: () => void
    daycare: Daycare
    create: boolean
    refetchSections: () => void
    // EDIT
    sectionId?: number
    sectionName?: string
    setSectionName?: (value: string) => void
    max?: string
    setMax?: (value: string) => void
    sectionGroupId?: number
    sectionGroups: Array<SectionGroup>
}

const SectionDialog: FunctionComponent<sectionDialogDialogProps> = ({
    open,
    handleClose,
    daycare,
    create,
    refetchSections,
    sectionId,
    sectionName,
    setSectionName,
    max,
    setMax,
    sectionGroupId,
    sectionGroups
}: sectionDialogDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const {
        openDialogActionItem: actionItem,
        dialog: addSectionDialog,
        reopenTrigger
    } = useDialog({
        dialogTitle: t('addStaffRate'),
        Component: StaffRateDialog,
        props: (onClose) => ({
            daycare: daycare,
            create: true,
            hideSections: true,
            addNewSectionGroupOption,
            refetchStaffRates: refetchSections,
            staffRateName: sectionName,
            handleClose: onClose
        }),
        resourceLabel: { type: 'addResource', label: t('staffRate') },
        hideDialogClose: true
    })

    const [newSectionName, setNewSectionName] = useState<string>(sectionName || '')
    const [newMaxSeats, setNewMaxSeats] = useState<string>(max || '1')
    const [validityMaxSeats, setValidityMaxSeats] = useState<boolean>(
        max ? Number(max) >= 1 && Number(max) <= 100 : true
    )

    const [newSectionGroupId, setNewSectionGroupId] = useState<number | undefined>(sectionGroupId)

    const [createSection] = useMutation(CREATE_SECTION, {
        variables: {
            daycare_id: daycare.id,
            name: newSectionName,
            max: parseInt(newMaxSeats)
        },
        onCompleted: async ({ createSection }) => {
            if (newSectionGroupId) {
                await setSectionGroupToSection({
                    variables: {
                        sectionId: createSection
                    }
                })
            }

            refetchSections()

            showNotificationSuccess(t('sectionAdded'))

            handleClose()

            setNewSectionName('')
            setNewMaxSeats('1')
            setNewSectionGroupId(undefined)
        },
        onError: () => {
            showNotificationError(t('errorAddedSection'))
        }
    })

    const [updateSection] = useMutation(UPDATE_SECTION, {
        variables: {
            section_id: sectionId,
            name: newSectionName,
            max: parseInt(newMaxSeats),
            daycare_id: daycare.id
        },
        onCompleted: async () => {
            if (daycare.staffAccess && newSectionGroupId) {
                await setSectionGroupToSection({
                    variables: {
                        sectionId: sectionId
                    }
                })
            }

            refetchSections()

            if (setSectionName) setSectionName(newSectionName)
            if (setMax) setMax(newMaxSeats)

            showNotificationSuccess(
                daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep
                    ? t('sectorUpdated')
                    : t('sectionUpdated')
            )

            handleClose()

            setNewSectionName('')
            setNewMaxSeats('1')
            setNewSectionGroupId(undefined)
        },
        onError: () => {
            showNotificationError(
                daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep
                    ? t('errorUpdatedSector')
                    : t('errorUpdatedSection')
            )
        }
    })

    const [setSectionGroupToSection] = useMutation(SET_SECTION_GROUP_TO_SECTION, {
        variables: {
            sectionGroupId: newSectionGroupId || 0,
            daycareId: daycare.id
        },
        onCompleted: () => {
            showNotificationSuccess(t('sectionGroupAdded'))

            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorAddedSectionGroup'))
        }
    })

    const sectionsGroupsOptions: SelectOptions<number | undefined> = [
        {
            options: sectionGroups.map(
                (sectionGroup): SelectOption<number> => ({
                    value: sectionGroup.id,
                    name: sectionGroup.name.valueOf()
                })
            )
        },
        {
            options: [
                {
                    value: undefined,
                    name: t('noStaffRate')
                }
            ]
        }
    ]

    function addNewSectionGroupOption(value: SectionGroup): void {
        refetchSections()
        setNewSectionGroupId(value.id)
    }

    const disabled: boolean = create ? newSectionName === '' || !validityMaxSeats : false

    return (
        <>
            <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
                <DialogHeader
                    title={
                        daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep
                            ? create
                                ? t('addSector')
                                : t('updateSector')
                            : create
                              ? t('addSection')
                              : t('updateSection')
                    }
                />
                <DialogBody className={styles.DialogBody}>
                    <div className={styles.InputContainer}>
                        <div className={styles.Input}>
                            <UserInput
                                label={t('name')}
                                value={newSectionName}
                                type='text'
                                required
                                fullWidth
                                format={'pascal'}
                                className={styles.UserInput}
                                setValue={setNewSectionName}
                            />
                            <UserInput
                                label={t('seatsNumber')}
                                value={newMaxSeats}
                                type='number'
                                required
                                fullWidth
                                format={'num'}
                                verify={'number'}
                                maxNumber={100}
                                minNumber={1}
                                className={styles.UserInput}
                                setValue={setNewMaxSeats}
                                setValid={setValidityMaxSeats}
                            />
                        </div>
                        {daycare.staffAccess && (
                            <>
                                <div className={styles.Input}>
                                    <SelectUnique
                                        selectOptions={sectionsGroupsOptions}
                                        selectValue={newSectionGroupId}
                                        setSelectValue={setNewSectionGroupId}
                                        label={t('management')}
                                        className={styles.UserInput}
                                        actionItem={actionItem}
                                        reopenTrigger={reopenTrigger}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        <ButtonTextIcon
                            type={create ? 'add' : 'update'}
                            onClick={create ? createSection : updateSection}
                            className={styles.Button}
                            disabled={disabled}
                        />
                    </div>
                    {addSectionDialog}
                </DialogBody>
            </Dialog>
        </>
    )
}

export default SectionDialog
